import React, { useState, useEffect, useRef } from "react";
import { WidgetProps } from "@rjsf/utils";
import {
  Button,
  Card,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@material-ui/core";
import { CloudUpload } from "@material-ui/icons";
import uploadFile from "../api/uploadFile";

interface CaptionUrlData {
  caption_file_url?: string;
}

const CaptionUrlWidget: React.FC<WidgetProps> = props => {
  const { value, onChange } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [fileContent, setFileContent] = useState<string>("");
  const [localData, setLocalData] = useState<CaptionUrlData>({ caption_file_url: value || "" });
  const [captionFileLanguage, setCaptionFileLanguage] = useState<string | undefined>();
  const localDataRef = useRef(localData);
  const id = props.id;

  useEffect(() => {
    setLocalData({ caption_file_url: value || "" });
  }, [value]);

  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  const { caption_file_url = "" } = localData;

  const updateCaptionData = (updates: Partial<CaptionUrlData>) => {
    const newData = { ...localData, ...updates };
    // https://d2dlev8jyes5k0.cloudfront.net/joint_academy/captions/01994c26-a805-7862-9897-623a320cc417/caption-en.vtt
    const lang = newData.caption_file_url?.split("-").pop()?.split(".").shift();
    if (lang) {
      setCaptionFileLanguage(lang);
    }
    setLocalData(newData);
    onChange(newData.caption_file_url || "");
  };

  return (
    <Card elevation={2} style={{ padding: "16px" }}>
      <div style={{ marginTop: "16px" }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Caption File Language</FormLabel>
          <RadioGroup value={captionFileLanguage} onChange={e => setCaptionFileLanguage(e.target.value)} row>
            <FormControlLabel value="sv" control={<Radio color="primary" />} label="Swedish" />
            <FormControlLabel value="en" control={<Radio color="primary" />} label="English" />
            <FormControlLabel value="fr" control={<Radio color="primary" />} label="French" />
            <FormControlLabel value="de" control={<Radio color="primary" />} label="German" />
            <FormControlLabel value="es" control={<Radio color="primary" />} label="Spanish" />
          </RadioGroup>
        </FormControl>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
        <TextField
          label={!captionFileLanguage ? "Select language first" : "Enter Caption file URL"}
          disabled={!captionFileLanguage || loading}
          value={caption_file_url}
          fullWidth
          style={{ flex: 1 }}
          onChange={e => updateCaptionData({ caption_file_url: e.target.value })}
        ></TextField>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <label htmlFor={"vtt-file-uploader_" + id} style={{ margin: 0 }}>
            <Button
              disabled={!captionFileLanguage || loading}
              variant="contained"
              color="primary"
              component="span"
              startIcon={<CloudUpload />}
            >
              Upload VTT
            </Button>
          </label>
        </div>
        <input
          id={"vtt-file-uploader_" + id}
          type="file"
          accept=".vtt"
          onChange={async e => {
            const file = e.target.files?.[0];

            if (file) {
              setLoading(true);
              try {
                const content = await file.text();
                setFileContent(content);

                const url = await uploadFile(file, captionFileLanguage);
                updateCaptionData({ caption_file_url: url });
              } catch (error) {
                console.error("Error uploading file:", error);
              } finally {
                setLoading(false);
              }
            }
          }}
          style={{ display: "none" }}
        />
      </div>

      {fileContent && (
        <div style={{ marginTop: "16px" }}>
          <TextField
            label="VTT Preview"
            value={fileContent}
            multiline
            minRows={4}
            maxRows={8}
            fullWidth
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { fontSize: "12px", fontFamily: "monospace" },
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default CaptionUrlWidget;
